import * as Sentry from '@sentry/react'

import { mapCreative } from '../campaignSetup/mappers'
import { mapIntegrationDetails, findByAssetClass } from '../integrationAssets/mappers'
import { campaignSetupsResponseSchema, defaultCampaignParametersResponseSchema } from './schemas'

// Per the guide's "Show campaign health" mapping. null (still syncing) stays
// null rather than defaulting to a color, so callers can render a distinct
// spinner/pending state instead of a wrong-colored badge.
const CAMPAIGN_STATUS_COLOR = { ACTIVE: 'green', PAUSED: 'yellow', ARCHIVED: 'grey' }

export function getCampaignHealthColor(campaignStatus) {
  return campaignStatus ? (CAMPAIGN_STATUS_COLOR[campaignStatus] ?? null) : null
}

function mapCampaignSetupStatus(setup) {
  return {
    channel: setup.channel,
    campaignStatus: setup.campaign_status ?? null,
    adGroupStatus: setup.ad_group_status ?? null,
    budgetAverage: setup.budget_average ?? null,
    healthColor: getCampaignHealthColor(setup.campaign_status),
  }
}

// The flat v2/v3 snapshot spells these two with hyphens; the nested form uses
// underscores (server-side counterpart: _FLAT_TO_NESTED_KEY_RENAMES).
const FLAT_TO_NESTED_KEY_RENAMES = {
  'postal-codes': 'postal_codes',
  'range-locations': 'range_locations',
}

// See schemas.js's tasteClusterTargetingSchema comment: event_campaign.targeting
// is platform-nested for native-v4/previously-edited TCs, but a flat
// single-channel snapshot for a legacy TC synthesized from v3 data and never
// edited since. This always returns the platform-nested form so a caller
// building an editPublishedTcAsync payload doesn't have to special-case
// legacy events itself. `channels` (the TC's live campaign_setups[].channel
// values) disambiguates which platform a flat snapshot belongs to; with more
// than one live channel and no way to tell from the flat snapshot alone which
// one it came from, this falls back to "fb" — matching the same preference
// synthesize_event_campaign_from_setups uses server-side.
export function normalizeTcTargeting(targeting, channels = []) {
  if (!targeting) return {}
  const { fb, google } = targeting
  if (fb || google) {
    return { ...(fb ? { fb } : {}), ...(google ? { google } : {}) }
  }
  const flatFields = {}
  Object.entries(targeting).forEach(([key, value]) => {
    if (key === 'tc' || key === 'tc_run_id') return
    flatFields[FLAT_TO_NESTED_KEY_RENAMES[key] ?? key] = value
  })
  if (!Object.keys(flatFields).length) return {}
  const channel = channels.includes('google') && !channels.includes('fb') ? 'google' : 'fb'
  return { [channel]: flatFields }
}

function mapTasteCluster(tc) {
  const campaignSetups = (tc.campaign_setups ?? []).map(mapCampaignSetupStatus)
  const channels = campaignSetups.map((s) => s.channel)

  return {
    tc: tc.tc,
    tcRunId: tc.tc_run_id,
    published: tc.published,
    campaignSetups,
    creative: tc.creative ? mapCreative(tc.creative) : null,
    targeting: tc.targeting ?? null,
    normalizedTargeting: normalizeTcTargeting(tc.targeting, channels),
  }
}

function mapEventCampaign(eventCampaign) {
  if (!eventCampaign) return null

  return {
    id: eventCampaign.id,
    totalBudget: eventCampaign.total_budget ?? null,
    goal: eventCampaign.goal ?? null,
    startDate: eventCampaign.start_date ?? null,
    endDate: eventCampaign.end_date ?? null,
    platforms: eventCampaign.platforms ?? [],
    status: eventCampaign.status ?? null,
    integrationDetails: eventCampaign.integration_details ?? [],
    campaignTargeting: eventCampaign.campaign_targeting ?? null,
    platformOverrides: eventCampaign.platform_overrides ?? null,
  }
}

// v4 GET /campaign_setups -> FE domain model. Explicitly does not carry
// forward the deprecated latest_campaign_setup shape (single-TC only, no
// campaign header, no per-channel status) — this is a from-scratch mapper,
// not an adaptation of the old one. eventCampaign is null when the event has
// no published TCs at all (not an error case).
export function mapCampaignSetupsResponse(data) {
  const parsed = campaignSetupsResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = (parsed.success ? parsed.data : data) ?? {}

  return {
    eventCampaign: mapEventCampaign(source.event_campaign),
    tasteClusters: (source.taste_clusters ?? []).map(mapTasteCluster),
  }
}

export function mapDefaultCampaignParameters(data) {
  const parsed = defaultCampaignParametersResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = (parsed.success ? parsed.data : data) ?? {}
  const integration_details = mapIntegrationDetails(source.integration_details ?? [])

  const conversion = findByAssetClass(integration_details, {
    platform: 'facebook',
    assetClass: 'conversion',
  })
  const leadForm = findByAssetClass(integration_details, {
    platform: 'facebook',
    assetClass: 'lead_form',
  })
  const customEventType = findByAssetClass(integration_details, {
    platform: 'facebook',
    assetClass: 'custom_event_type',
  })

  return {
    total_budget: source.total_budget ?? null,
    goal: source.goal ?? null,
    tcs: source.tcs ?? [],
    integration_details,
    meta_access_error: source.meta_access_error ?? false,
    conversion_id: conversion?.asset_id,
    lead_form_id: leadForm?.asset_id,
    custom_event_type: customEventType?.asset_id,
  }
}

export function mapPublishedCampaignSetupsError(error, status) {
  const body = error?.response?.data ?? error
  return {
    message: body?.message ?? body?.error_message ?? 'Unknown error',
    code: body?.error_code ?? body?.code ?? null,
    status: status ?? error?.response?.status ?? null,
    data: body?.data,
  }
}
